import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ArchiveUserParams {
  contactId: string;
  userId?: string | null;
  reason?: string;
}

interface RestoreUserParams {
  contactId: string;
  userId?: string | null;
}

export const useArchiveUser = () => {
  const queryClient = useQueryClient();

  const archiveUser = useMutation({
    mutationFn: async ({ contactId, userId, reason }: ArchiveUserParams) => {
      console.log("📦 Archiving contact:", contactId, "with user_id:", userId);

      // Step 1: Update profiles table to mark as archived
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          archived: true,
          archived_at: new Date().toISOString(),
          archived_reason: reason || null
        })
        .eq('id', contactId);

      if (profileError) {
        console.error("Error archiving profile:", profileError);
        throw new Error(profileError.message);
      }

      // Step 2: If user has a user account, update their role to 'archived'
      if (userId) {
        // First, get current role to store in previous_role
        const { data: currentRole, error: roleQueryError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', userId)
          .maybeSingle();

        if (roleQueryError) {
          console.error("Error fetching current role:", roleQueryError);
          // Don't throw - profile is already archived
        }

        if (currentRole && currentRole.role !== 'archived') {
          // Update role to archived and store previous role
          const { error: roleError } = await supabase
            .from('user_roles')
            .update({
              previous_role: currentRole.role,
              role: 'archived'
            })
            .eq('user_id', userId);

          if (roleError) {
            console.error("Error updating user role to archived:", roleError);
            // Don't throw - profile is already archived
          } else {
            console.log("✅ User role updated to 'archived', previous role stored:", currentRole.role);
          }
        }
      }

      return { contactId, userId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["organization-contacts"] });
      queryClient.invalidateQueries({ queryKey: ["user-roles"] });
      toast.success("Contact archived successfully");
    },
    onError: (error: Error) => {
      console.error("Archive error:", error);
      toast.error(`Failed to archive contact: ${error.message}`);
    }
  });

  const restoreUser = useMutation({
    mutationFn: async ({ contactId, userId }: RestoreUserParams) => {
      console.log("♻️ Restoring contact:", contactId, "with user_id:", userId);

      // Step 1: Update profiles table to clear archived status
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          archived: false,
          archived_at: null,
          archived_reason: null
        })
        .eq('id', contactId);

      if (profileError) {
        console.error("Error restoring profile:", profileError);
        throw new Error(profileError.message);
      }

      // Step 2: If user has a user account, restore their previous role
      if (userId) {
        // Get the previous role
        const { data: roleData, error: roleQueryError } = await supabase
          .from('user_roles')
          .select('previous_role')
          .eq('user_id', userId)
          .maybeSingle();

        if (roleQueryError) {
          console.error("Error fetching previous role:", roleQueryError);
        }

        const previousRole = roleData?.previous_role || 'standard';

        // Restore the role
        const { error: roleError } = await supabase
          .from('user_roles')
          .update({
            role: previousRole,
            previous_role: null
          })
          .eq('user_id', userId);

        if (roleError) {
          console.error("Error restoring user role:", roleError);
          // Don't throw - profile is already restored
        } else {
          console.log("✅ User role restored to:", previousRole);
        }
      }

      return { contactId, userId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["organization-contacts"] });
      queryClient.invalidateQueries({ queryKey: ["user-roles"] });
      toast.success("Contact restored successfully");
    },
    onError: (error: Error) => {
      console.error("Restore error:", error);
      toast.error(`Failed to restore contact: ${error.message}`);
    }
  });

  return { archiveUser, restoreUser };
};
